import './wish-list-add-all-to-cart';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { PageTypes } from '../../../common/utils/page-util';
import { ProductCardData } from '../../product-card/product-card-display';
import { StoreState } from '../../../store/store.interface';
import { WishList, WishListItem } from '../interfaces/wish-list-items.interface';
import { connect } from 'pwa-helpers';
import { css, html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getWishListData } from '../store/wish-list.selector';
import { getPaginationDetails, getPaginationMessage } from '../../pagination/utils/pagination.utils';
import {
  _renderLoadingTemplate,
  hasWishListProducts,
  isWishListFailure,
  isWishListSuccess,
  renderNoWishListItemsTemplate,
} from '../utils/wish-list.util';
import { mapWishListItemToProductCardData } from '../mapping/wish-list-product-card.mapping';
import { fetchWishListByWishListId } from '../thunks/wish-list-fetch-wish-list-by-id.thunks';
import { renderErrorToast } from '../utils/wish-list-toast.util';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';

const MAX_ITEMS_PAGE = 10;

/**
 * Allows the user to review a wish list and its products from the off-canvas.
 */
@customElement('mhecomm-review-wish-list')
export class ReviewWishList extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .wish-list-products {
        max-height: calc(65vh + 2rem);
        overflow-x: hidden;
        overflow-y: auto;
        scrollbar-color: grey transparent;
      }

      .pagination-container {
        height: 5.5rem;
      }
    `,
  ];

  /**
   * The initial wish list before being refreshed.
   */
  @property({ type: Object })
  wishListData?: WishList;

  /**
   * The current page of the pagination.
   */
  @state()
  private currentPage = 0;

  /**
   * A flag to indicate if the wish list fetch call failed.
   */
  @state()
  private wishListFetchCallFailed = false;

  /**
   * The loading status of the wish list.
   */
  @state()
  private wishListLoadingOrIdle = false;

  /**
   * The wish list to review.
   */
  @state()
  private wishList?: WishList = undefined;

  /**
   * The index of the first product to show in the pagination.
   */
  private firstOfPagination = 1;

  /**
   * The index of the last product to show in the pagination.
   */
  private lastOfPagination = 1;

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(this.shadowRoot!.host, 'go-to-page-in-memory', (e: Event) =>
      this.updatePaginationData((e as CustomEvent).detail.paginationNumber)
    );
  }

  updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    this.refreshWishList(changedProperties);
  }

  /**
   * Triggers wish list load when the wish list has changed or its products have changed.
   *
   * This is necessary as for example comments could have changed, but the state is not up-to-date.
   */
  refreshWishList(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (changedProperties.has('wishListData') && this.wishListData) {
      this.wishListLoadingOrIdle = true;
      store.dispatch(fetchWishListByWishListId(Number(this.wishListData.wishlist_id))).finally((): void => {
        this.wishListLoadingOrIdle = false;
      });
      this.currentPage = 0;
    }
  }

  stateChanged(): void {
    if (isWishListSuccess()) {
      this.wishList = getWishListData(store.getState() as StoreState);
      hasWishListProducts(this.wishList!) && this.updatePaginationData();
    }
    this.wishListFetchCallFailed = isWishListFailure();
  }

  renderComp(): TemplateResult {
    if (this.wishListLoadingOrIdle) {
      return html` <div class="p-3">${_renderLoadingTemplate()}</div>`;
    }

    if (this.wishListFetchCallFailed) {
      return html` <div class="p-3">
        ${renderErrorToast({
          error: ErrorToastWishList.FETCHING_WISH_LIST_ITEMS,
          displayLoadingTemplate: true,
          autoHide: false,
          hideClose: true,
        })}
      </div>`;
    }

    if (!hasWishListProducts(this.wishList!)) {
      return html` <div class="p-3">
        ${renderNoWishListItemsTemplate({
          title: 'Your list is empty!',
          message: 'Start adding items to revisit them and share with others.',
        })}
      </div>`;
    }

    return html`
      <div class="position-relative">
        <div class="my-3">
          ${this.renderPaginationMessage()} ${this.renderAddAllToCartButton()}
        </div>
        <div class="container-fluid pe-md-1 wish-list-products">
          ${this.getProductsToRender().map(
            (productToRender, index) =>
              html` <div
                id=${`wishlist-${this.wishList!.wishlist_id}-product-${productToRender.mhid}`}
                class="${index > 0 ? 'mt-3' : ''}"
              >
                <mhecomm-wish-list-item-card-display
                  .offCanvasView=${true}
                  .productCardData=${productToRender}
                  .wishList="${this.wishList}"
                >
                </mhecomm-wish-list-item-card-display>
              </div>`
          )}
        </div>
        <div class="bg-white py-2 position-sticky bottom-0 pagination-container">
          ${this.renderPaginationInMemory()}
        </div>
      </div>
    `;
  }

  /**
   * Returns the products to render based on the current pagination.
   */
  getProductsToRender(): ProductCardData[] {
    const newList: ProductCardData[] = [];

    for (let i = this.firstOfPagination - 1; i < this.lastOfPagination; i++) {
      const wishListItem: WishListItem | undefined = this.wishList!.wishlist_items[i];
      if (wishListItem) {
        newList.push(mapWishListItemToProductCardData(wishListItem));
      }
    }

    return newList;
  }

  /**
   * Checks if the wish list has pagination.
   */
  private hasPagination(): boolean {
    return hasWishListProducts(this.wishList!) && this.wishList!.wishlist_items.length > MAX_ITEMS_PAGE;
  }

  /**
   * Renders the pagination message at the top of the list.
   */
  private renderPaginationMessage(): TemplateResult | typeof nothing {
    if (!this.hasPagination()) {
      return nothing;
    }

    return html`
      <div class="d-flex px-3 mt-2 float-start">
        <mhecomm-pagination-message
          ?useRawMessage=${true}
          message="${getPaginationMessage(this.currentPage, MAX_ITEMS_PAGE, this.getWishListItemsCount())}"
          id="pagination_message"
          data-automation-id="pagination_message"
        >
        </mhecomm-pagination-message>
      </div>
    `;
  }

  /**
   * Renders the pagination and pagination message at the bottom of the list.
   */
  private renderPaginationInMemory(): TemplateResult | typeof nothing {
    if (!this.hasPagination()) {
      return nothing;
    }

    return html`
      <mhecomm-pagination-in-memory
        .currentPage=${this.currentPage}
        .pageLength=${MAX_ITEMS_PAGE}
        .total=${this.getWishListItemsCount()}
        layout="vertical"
        maxPagesShown="3"
      ></mhecomm-pagination-in-memory>
    `;
  }

  /**
   * It should be used when the pagination occurs on the pagination-in-memory
   * or when some wishlist is removed, because we might need to update the results
   * based on the new total wish lists
   *
   * @param paginationNumber - The page to navigate for or the current page if
   * a wish list was removed
   */
  private updatePaginationData(paginationNumber?: number) {
    const page = paginationNumber ? paginationNumber - 1 : this.currentPage;
    const { currentPage, firstOfPage, lastOfPage } = getPaginationDetails(
      page,
      MAX_ITEMS_PAGE,
      this.getWishListItemsCount()
    );
    this.firstOfPagination = firstOfPage;
    this.lastOfPagination = lastOfPage;
    this.currentPage = currentPage;
  }

  private renderAddAllToCartButton() {
    return html`
      <div class="d-flex px-2 flex-end justify-content-end">
        <mhecomm-wish-list-add-all-to-cart-button
          .wishList=${this.wishList}
          pageType=${PageTypes.CatalogLandingPage}
          ?productCatalogueOffCanvas=${true}
        ></mhecomm-wish-list-add-all-to-cart-button>
      </div>
    `;
  }

  /**
   * Returns the total wishlist count.
   */
  private getWishListItemsCount(): number {
    return this.wishList?.wishlist_items?.length ?? 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-review-wish-list': ReviewWishList;
  }
}
